/**
 * Intel extras' view-models (dashboard spec 2026-10-09 §2.2 tab 6, W4 card C5): Markets, Stations and Charts. Pure
 * functions over plain inputs, read-only, run at the dashboard refresh rate while it is open (never per frame).
 *
 * - **Markets:** one row per station of this system, read live from the strategic system view (the docked station
 *   first, "Here · live"), then one row per other system from the pilot's price log (`career.priceLog`, TR-1b): the
 *   best buy and sell per ware and, when a cached view names it, the station that quoted it. The log keeps no stock and
 *   no per-station prices (D-487 decision 2), and only a covered system reads live (RD9, D-494): the map price layers'
 *   own rule. Ages use C0's `lastSeen` stamp (`t`, career game seconds).
 * - **Stations:** the existing `StationPanel` model (`stationPanelModel`) per station: this system's live, other
 *   systems' from the views the pilot saw this session (memory only, no save field) with the game time they were read;
 *   visited stations with no such view list their name and last-seen age only.
 * - **Charts:** unsold chart value per system (`ReachState.charts.unsold`), first discoveries marked. Every chart
 *   buyer pays the full value (`exploration/commands.ts sellCharts`), so "the last-seen buyer's price" is that value.
 */
import type { PriceLogEntry } from '../../../../career/types'
import type { MarketRow, StationView } from '../../../../strategic/types'
import { WARE_IDS } from '../../../../strategic/wares'
import { formatCr } from '../../../format'
import { causeChipModel, stockModel, trendModel, type CauseChipModel, type StockModel, type TrendModel } from '../../../strategic/causeModel'
import { wareName } from '../../../strategic/names'
import { stationPanelModel, type StationPanelModel } from '../../../strategic/stationPanelModel'
import type { DashboardConfig } from '../../config'
import { lastSeen } from '../../data'
import type { SeenSource } from '../../data/types'
import { ageStamp, type Age } from '../../parts'
import type { IntelConfig } from './config'

const NO_DATA = -1

export function fill(template: string, values: Readonly<Record<string, string | number>>): string {
  return template.replace(/\{(\w+)\}/g, (m, k: string) => {
    const v = values[k]
    return v === undefined ? m : String(v)
  })
}

/** "1 system" or "3 systems": the singular at exactly one, else the plural with {n} filled. */
export function countText(n: number, forms: { readonly one: string; readonly many: string }): string {
  return n === 1 ? forms.one : fill(forms.many, { n })
}

/** A system's stations as the strategic view lists them (only what the models read). */
export interface ViewStations {
  readonly systemId: string
  readonly stations: readonly StationView[]
}

/** What every Intel model shares: the configs, the clock and the names. */
export interface IntelBase {
  readonly cfg: IntelConfig
  readonly age: DashboardConfig['age']
  /** Career game seconds (`career.gameTime`). */
  readonly now: number
  systemName(id: string): string
}

/** The live age line of a station in this system: "Here · live" docked there, else "This system · live". */
function liveAge(base: IntelBase, station: { id: string; name: string }, dockedStationId: string | null): Age {
  if (dockedStationId === station.id) return ageStamp(base.age, station.name, null, base.now, true)
  return { level: 'fresh', text: base.cfg.liveHere }
}

function byName<T extends { readonly title: string }>(a: T, b: T): number {
  return a.title < b.title ? -1 : a.title > b.title ? 1 : 0
}

// ---------------------------------------------------------------------------------------------
// Markets
// ---------------------------------------------------------------------------------------------

export interface MarketsInput extends IntelBase {
  /** This system's live view (null outside a system or before the first read). */
  readonly here: ViewStations | null
  /** The strategic id of the station the pilot is docked at (null in flight). */
  readonly dockedStationId: string | null
  readonly priceLog: Readonly<Record<string, PriceLogEntry>> | undefined
  /** Views seen this session, by system: they only name a logged price's station, never price it (RD9). */
  readonly named: ReadonlyMap<string, ViewStations>
}

export interface WareLine {
  readonly ware: string
  readonly name: string
  /** What the pilot pays per t ("412 cr"), or the none mark. */
  readonly buy: string
  /** What the station pays per t, or the none mark. */
  readonly sell: string
  /** "at Varos Yard" for a logged price whose station is known; null otherwise. */
  readonly buyAt: string | null
  readonly sellAt: string | null
  /** Live rows only. */
  readonly stock: StockModel | null
  readonly trend: TrendModel | null
  readonly chip: CauseChipModel | null
}

export interface MarketEntry {
  readonly key: string
  readonly title: string
  /** "Harrow's Reach" (a live station's system) or "Best known prices" (a logged system). */
  readonly sub: string
  readonly age: Age
  readonly live: boolean
  /** "6 wares · 2 flagged prices". */
  readonly summary: string
  /** Sorted by ware name. */
  readonly wares: readonly WareLine[]
}

export interface MarketsModel {
  readonly rows: readonly MarketEntry[]
}

function money(n: number | null, none: string): string {
  return n === null || !Number.isFinite(n) ? none : formatCr(n)
}

function liveWare(cfg: IntelConfig, row: MarketRow): WareLine {
  const none = cfg.markets.none
  return {
    ware: row.ware,
    name: wareName(row.ware),
    buy: money(row.buyUnit, none),
    sell: money(row.sellUnit, none),
    buyAt: null,
    sellAt: null,
    stock: stockModel(row),
    trend: trendModel(row.trend),
    chip: causeChipModel(row.cause),
  }
}

function summary(cfg: IntelConfig, wares: readonly WareLine[]): string {
  const flagged = wares.filter((w) => w.chip !== null).length
  const n = countText(wares.length, cfg.markets.wares)
  return flagged === 0 ? n : `${n} · ${countText(flagged, cfg.markets.shocks)}`
}

function sortWares(wares: WareLine[]): WareLine[] {
  return wares.sort((a, b) => (a.name < b.name ? -1 : a.name > b.name ? 1 : 0))
}

function loggedWares(cfg: IntelConfig, entry: PriceLogEntry, named: ViewStations | undefined): WareLine[] {
  const out: WareLine[] = []
  const none = cfg.markets.none
  const at = (i: number | undefined): string | null => {
    const st = i === undefined || i === NO_DATA ? undefined : named?.stations[i]
    return st === undefined ? null : fill(cfg.markets.at, { station: st.name })
  }
  WARE_IDS.forEach((ware, w) => {
    const b = entry.b[w] ?? NO_DATA
    const s = entry.s[w] ?? NO_DATA
    if (b === NO_DATA && s === NO_DATA) return
    out.push({
      ware,
      name: wareName(ware),
      buy: b === NO_DATA ? none : formatCr(b),
      sell: s === NO_DATA ? none : formatCr(s),
      buyAt: b === NO_DATA ? null : at(entry.bAt[w]),
      sellAt: s === NO_DATA ? null : at(entry.sAt[w]),
      stock: null,
      trend: null,
      chip: null,
    })
  })
  return sortWares(out)
}

/** Markets: this system's stations live (docked first), then the logged systems, most recently seen first. */
export function marketsModel(m: MarketsInput): MarketsModel {
  const rows: MarketEntry[] = []
  const hereId = m.here?.systemId ?? null
  if (m.here !== null) {
    const sys = m.systemName(m.here.systemId)
    const live: MarketEntry[] = []
    for (const st of m.here.stations) {
      if (!st.dockable) continue
      const wares = sortWares(st.wares.filter((r) => r.buyUnit !== null || r.sellUnit !== null).map((r) => liveWare(m.cfg, r)))
      live.push({ key: st.id, title: st.name, sub: sys, age: liveAge(m, st, m.dockedStationId), live: true, summary: summary(m.cfg, wares), wares })
    }
    live.sort((a, b) => (a.key === m.dockedStationId ? -1 : b.key === m.dockedStationId ? 1 : byName(a, b)))
    rows.push(...live)
  }
  const logged: { at: number; row: MarketEntry }[] = []
  for (const [systemId, entry] of Object.entries(m.priceLog ?? {})) {
    if (systemId === hereId) continue
    const title = m.systemName(systemId)
    const wares = loggedWares(m.cfg, entry, m.named.get(systemId))
    const age = ageStamp(m.age, title, entry.t, m.now, false)
    logged.push({ at: entry.t, row: { key: systemId, title, sub: m.cfg.markets.logged, age, live: false, summary: summary(m.cfg, wares), wares } })
  }
  logged.sort((a, b) => b.at - a.at || byName(a.row, b.row))
  for (const l of logged) rows.push(l.row)
  return { rows }
}

// ---------------------------------------------------------------------------------------------
// Stations
// ---------------------------------------------------------------------------------------------

/** A system view read this session, with the career game time it was read. */
export interface SeenView extends ViewStations {
  readonly at: number
}

export interface StationsInput extends IntelBase {
  readonly here: ViewStations | null
  readonly dockedStationId: string | null
  /** Other systems' views seen this session (memory only). */
  readonly seen: ReadonlyMap<string, SeenView>
  /** Known stations (C0's directory) for visited systems with no view this session. */
  readonly known: readonly { readonly id: string; readonly name: string; readonly systemId: string }[]
  /** The `lastSeen` source: the price log's stamps (which systems the pilot visited, and when). */
  readonly seenSource: SeenSource
}

export interface StationEntry {
  readonly key: string
  readonly title: string
  readonly systemName: string
  readonly age: Age
  readonly live: boolean
  /** The Station panel's model; null when the economy was not read this session. */
  readonly panel: StationPanelModel | null
}

export interface StationsModel {
  readonly rows: readonly StationEntry[]
}

/** Stations: this system's live (docked first), then each other visited system's, most recently seen first. */
export function stationsModel(m: StationsInput): StationsModel {
  const rows: StationEntry[] = []
  const hereId = m.here?.systemId ?? null
  if (m.here !== null) {
    const sys = m.systemName(m.here.systemId)
    const live: StationEntry[] = []
    for (const st of m.here.stations) {
      if (!st.dockable) continue
      live.push({ key: st.id, title: st.name, systemName: sys, age: liveAge(m, st, m.dockedStationId), live: true, panel: stationPanelModel(st) })
    }
    live.sort((a, b) => (a.key === m.dockedStationId ? -1 : b.key === m.dockedStationId ? 1 : byName(a, b)))
    rows.push(...live)
  }
  const others: { at: number; row: StationEntry }[] = []
  const listed = new Set<string>()
  for (const [systemId, view] of m.seen) {
    if (systemId === hereId) continue
    const sys = m.systemName(systemId)
    for (const st of view.stations) {
      if (!st.dockable) continue
      listed.add(st.id)
      others.push({ at: view.at, row: { key: st.id, title: st.name, systemName: sys, age: ageStamp(m.age, st.name, view.at, m.now, false), live: false, panel: stationPanelModel(st) } })
    }
  }
  for (const st of m.known) {
    if (st.systemId === hereId || listed.has(st.id) || m.seen.has(st.systemId)) continue
    const seen = lastSeen(m.seenSource, st.systemId, 'economy', m.now)
    if (seen.at === null) continue
    others.push({ at: seen.at, row: { key: st.id, title: st.name, systemName: m.systemName(st.systemId), age: ageStamp(m.age, st.name, seen.at, m.now, false), live: false, panel: null } })
  }
  others.sort((a, b) => b.at - a.at || (a.row.systemName < b.row.systemName ? -1 : a.row.systemName > b.row.systemName ? 1 : byName(a.row, b.row)))
  for (const o of others) rows.push(o.row)
  return { rows }
}

// ---------------------------------------------------------------------------------------------
// Charts
// ---------------------------------------------------------------------------------------------

export interface ChartsInput {
  readonly cfg: IntelConfig
  /** `ReachState.charts`: unsold value per system and the first discoveries. */
  readonly unsold: Readonly<Record<string, number>>
  readonly firstDiscoveries: readonly string[]
  /** `explore.json charts.firstDiscoveryMult`. */
  readonly firstMult: number
  systemName(id: string): string
}

export interface ChartRow {
  readonly systemId: string
  readonly name: string
  readonly value: string
  /** "First discovery ×2" or null. */
  readonly first: string | null
}

export interface ChartsModel {
  /** Highest value first. */
  readonly rows: readonly ChartRow[]
  readonly total: string
  readonly count: string
  readonly totalValue: number
}

export function chartsModel(m: ChartsInput): ChartsModel {
  const entries = Object.entries(m.unsold).filter(([, v]) => v > 0 && Number.isFinite(v))
  const named = entries.map(([id, v]) => ({ id, v, name: m.systemName(id) }))
  named.sort((a, b) => b.v - a.v || (a.name < b.name ? -1 : a.name > b.name ? 1 : 0))
  let total = 0
  for (const e of named) total += e.v
  const first = fill(m.cfg.charts.first, { mult: m.firstMult })
  return {
    rows: named.map((e) => ({ systemId: e.id, name: e.name, value: formatCr(e.v), first: m.firstDiscoveries.includes(e.id) ? first : null })),
    total: formatCr(total),
    count: countText(named.length, m.cfg.charts.systems),
    totalValue: total,
  }
}
